import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { ValuationEstimateRepository } from 'interfaceAdapter/repositories/ValuationEstimate';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        revenue_mjpy: yup.number().required().onlyNumber(),
        operating_income_mjpy: yup.number().required().onlyNumber(),
        depreciation_mjpy: yup.number().required().onlyNumber(),
        cash_and_deposits_mjpy: yup.number().required().onlyNumber(),
        interest_bearing_debt_mjpy: yup.number().required().onlyNumber(),
        net_assets_mjpy: yup.number().required().onlyNumber(),

        has_intellectual_property: yup.boolean().required(),

        ip_right_holder_type: yup.string().when('has_intellectual_property', {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
    })
    .required();

type ValuationEstimateForm = yup.InferType<typeof schema>;

export const useValuationEstimateForm = () => {
    const diContainer = useContext(diContainerContext);
    const valuationEstimateRepository = diContainer.resolve(ValuationEstimateRepository);
    const { loading, setLoading } = useLoading();
    const [complete, setComplete] = useState(false);
    const [priceRange, setPriceRange] = useState<{ lower: number; upper: number } | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm<ValuationEstimateForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            has_intellectual_property: false,
            ip_right_holder_type: undefined,
        },
    });

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            const result = await valuationEstimateRepository.post(
                data.revenue_mjpy as number,
                data.operating_income_mjpy as number,
                data.depreciation_mjpy as number,
                data.cash_and_deposits_mjpy as number,
                data.interest_bearing_debt_mjpy as number,
                data.net_assets_mjpy as number,
                data.has_intellectual_property,
                data.ip_right_holder_type || null,
            );
            setPriceRange(result);
            setComplete(true);
        } catch (error) {
            console.error('企業価値計算の送信エラー:', error);
        } finally {
            setLoading(false);
        }
    });

    // 結果が表示される時にスクロールをトップに戻す
    useEffect(() => {
        if (complete) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [complete]);

    return {
        control,
        errors,
        submit,
        loading,
        complete,
        priceRange,
        getValues,
    };
};
